import { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { ensureChartsRegistered } from './register'
import { CHART_COLORS, useDarkMode } from './palette'

ensureChartsRegistered()

export interface LineSeries {
  label: string
  values: Array<number | null>
  color?: string
  fill?: boolean
  dashed?: boolean
}

export interface LineChartProps {
  labels: string[]
  series: LineSeries[]
  height?: number | string
  yBeginAtZero?: boolean
  className?: string
  ariaLabel?: string
}

export function LineChart({
  labels,
  series,
  height = 280,
  yBeginAtZero = true,
  className,
  ariaLabel,
}: LineChartProps) {
  const dark = useDarkMode()

  const data = useMemo<ChartData<'line'>>(
    () => ({
      labels,
      datasets: series.map((s) => ({
        label: s.label,
        data: s.values,
        borderColor: s.color ?? CHART_COLORS.primary,
        backgroundColor: s.fill ? CHART_COLORS.primarySoft : s.color ?? CHART_COLORS.primary,
        fill: s.fill ?? false,
        tension: 0.3,
        borderDash: s.dashed ? [5, 5] : undefined,
        pointRadius: 3,
        pointHoverRadius: 5,
      })),
    }),
    [labels, series],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight },
        },
        y: {
          beginAtZero: yBeginAtZero,
          grid: { color: dark ? CHART_COLORS.gridDark : CHART_COLORS.gridLight },
          ticks: { color: dark ? CHART_COLORS.tickDark : CHART_COLORS.tickLight },
        },
      },
      plugins: {
        legend: { display: false },
        datalabels: { display: false },
      },
    }),
    [dark, yBeginAtZero],
  )

  return (
    <div
      className={className}
      style={{ height, width: '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      <Line data={data} options={options} />
    </div>
  )
}
